import React, { FC } from 'react'
import { Chip, Tooltip } from '@mui/material'
import format from 'date-fns/format'
import { GridColDef } from '@mui/x-data-grid-premium'
import { SensorUpload } from 'src/api'
import { InnerLink } from 'src/components/ui'
import { getSensorUploadStatusMeta } from './sensorUploadHelpers'

export const getSensorUploadFileName = (upload: SensorUpload): string => upload.file?.originalFileName || `Upload #${upload.id}`

interface SensorUploadStatusChipProps {
  status?: SensorUpload['status']
  errorMessage?: string | null
}

export const SensorUploadStatusChip: FC<SensorUploadStatusChipProps> = ({ status, errorMessage }) => {
  const { label, color, description } = getSensorUploadStatusMeta(status)
  const tooltip = [description, errorMessage].filter(Boolean).join('. ')
  const chip = <Chip label={label} color={color} size="small" />
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip
}

export namespace SensorUploadColumnsDataGrid {
  export const Id: GridColDef<SensorUpload> = {
    field: 'id',
    headerName: 'ID',
    type: 'string',
    minWidth: 100,
    editable: false,
    sortable: false
  }

  export const FileName: GridColDef<SensorUpload> = {
    field: 'fileName',
    headerName: 'File name',
    type: 'string',
    minWidth: 280,
    flex: 1,
    editable: false,
    sortable: false,
    valueGetter: ({ row }) => getSensorUploadFileName(row),
    renderCell: ({ value, row }) => <InnerLink to={`/sensors/uploads/${row.id}`}>{value}</InnerLink>
  }

  export const Author: GridColDef<SensorUpload> = {
    field: 'author',
    headerName: 'Author',
    type: 'string',
    minWidth: 180,
    editable: false,
    sortable: false,
    valueGetter: ({ row }) => row.author?.name || '-',
    renderCell: ({ value, row }) => (row.author?.id ? <InnerLink to={`/users/${row.author.id}`}>{value}</InnerLink> : value)
  }

  export const Status: GridColDef<SensorUpload> = {
    field: 'status',
    headerName: 'Status',
    type: 'string',
    minWidth: 150,
    editable: false,
    sortable: false,
    valueGetter: ({ row }) => getSensorUploadStatusMeta(row.status).label,
    renderCell: ({ row }) => <SensorUploadStatusChip status={row.status} errorMessage={row.errorMessage} />
  }

  export const CreatedCount: GridColDef<SensorUpload> = {
    field: 'createdCount',
    headerName: 'Sensors created',
    type: 'number',
    minWidth: 130,
    editable: false,
    sortable: false,
    align: 'left',
    headerAlign: 'left'
  }

  export const SkippedCount: GridColDef<SensorUpload> = {
    field: 'skippedCount',
    headerName: 'Sensors skipped',
    type: 'number',
    minWidth: 130,
    editable: false,
    sortable: false,
    align: 'left',
    headerAlign: 'left'
  }

  export const ImportedAt: GridColDef<SensorUpload> = {
    field: 'createdAt',
    headerName: 'Imported at',
    type: 'string',
    minWidth: 200,
    editable: false,
    sortable: false,
    valueFormatter: ({ value }) => (value ? format(new Date(value), 'PPpp') : '-')
  }
}
